import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { zodResolver } from "@hookform/resolvers/zod";
import { QRCodeSVG } from "qrcode.react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useWizard } from "react-use-wizard";
import { z } from "zod";

import EditorInputField from "@/components/forms/EditorInputField";
import FormButton from "@/components/FormSubmitButton";
import GoogleAutocomplete from "@/components/GoogleAutocomplete";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ExpectedError } from "@/lib/errors";
import { QRTypeInfo } from "@/lib/qr/constants";
import { sendEmailWithQRCode } from "@/lib/queries/QRQueries";
import useQR from "@/lib/stores/qr";
import { api, cn, handleApiRequest } from "@/lib/utils";
import { companyNameSchema } from "@/lib/zod-schemas/company-name";

function EmptyGoogleBizSearch({ onSearchCompany }) {
  return (
    <div className="flex flex-col items-center">
      <Image alt="Globe" width={24} height={24} className="w-6 h-6 mb-2" src="/globe.svg" />
      <p className="font-semibold mb-1">We couldn&apos;t find your company</p>
      <p className="text-left text-ash-500">Please, try searching for another name</p>
      <Button onClick={onSearchCompany} className="w-full mt-4 bg-black text-white text-base font-semibold" size="lg">
        Find my company
      </Button>
    </div>
  );
}

const validationSchema = z.object({
  companyName: companyNameSchema,
  city: z.string().optional(),
  state: z.string().optional(),
});

export function GoogleBizSearch() {
  const { nextStep } = useWizard();
  const { setState } = useQR();
  const [shouldRenderEmptyState, setShouldRenderEmptyState] = useState(false);

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      companyName: "",
      city: "",
      state: "",
    },
  });

  const onFormSubmit = async (data) =>
    handleApiRequest({
      makeRequest: async () => {
        if (!data.companyName) {
          toast.error("Company name is required");
          return;
        }

        if (!data.city || !data.state) {
          toast.error("Please enter a location");
          return;
        }

        const res = await api.get("/api/places/search", {
          params: {
            companyName: data.companyName,
            city: data.city,
            state: data.state,
          },
        });

        if (res.data.length === 0) {
          setShouldRenderEmptyState(true);
          throw new ExpectedError("No results found. Please try again with a different name.");
        }

        setState("companySuggestions", res.data);
        setState("companyName", data.companyName);
        nextStep();
      },
    });

  const onSearchCompany = () => {
    setShouldRenderEmptyState(false);
  };

  if (shouldRenderEmptyState) {
    return <EmptyGoogleBizSearch onSearchCompany={onSearchCompany} />;
  }

  return (
    <>
      <Image src="/qr-location-img.svg" className="w-full" width={400} height={142} alt="Location map" priority />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)} className="mt-8 text-left">
          <EditorInputField
            required
            form={form}
            fieldName="companyName"
            labelClassName="font-bold text-black text-xl"
            inputClassName="mt-1 h-9 text-sm"
            label="What is your company name?"
            placeholder="Company Name"
          />

          <label htmlFor="city" className="font-bold text-black text-xl">
            What is your primary service area?
          </label>
          <GoogleAutocomplete
            onPlaceChanged={(placeData) => {
              form.setValue("city", placeData.cityCountyNeighborhood);
              form.setValue("state", placeData.state);
            }}
            inputClassName="h-9 mt-1 text-sm mb-0"
            placeholder="City or county or neighborhood"
          />

          <FormButton
            formState={form.formState}
            size="lg"
            className="w-full mt-12 bg-black text-white text-base font-semibold"
          >
            Find my company
          </FormButton>
        </form>
      </Form>
    </>
  );
}

export function GenerateQR() {
  const { nextStep, previousStep } = useWizard();
  const { setState, companySuggestions, selectedCompany, type } = useQR();

  const onSubmit = async () => {
    const url = QRTypeInfo[type].getUrl(selectedCompany[QRTypeInfo[type].urlAccessProperty]);
    setState("QRCodeUrl", url);
    nextStep("shareQR");
  };

  const goBack = () => {
    toast("Please try searching a slightly different name or reach out for help!", {
      icon: "🔍",
    });
    setState("selectedCompany", null);
    previousStep();
  };

  return (
    <>
      <ul className="flex flex-col gap-3 mb-4">
        {companySuggestions
          .filter((s) => !!s?.data_id)
          .map((s, index) => (
            <li
              key={index}
              className={cn(
                "flex items-center justify-between py-3 px-4 border rounded-md border-ash-200",
                selectedCompany?.data_id == s.data_id && "ring-[3px] ring-rebolt-50"
              )}
            >
              <div className="flex gap-1 flex-col justify-start max-w-[60%] text-left">
                {s.data_cid ? (
                  <a
                    href={`https://www.google.com/maps/place/?cid=${s.data_cid}`}
                    target="_blank"
                    className="text-sm font-semibold leading-normal text-black whitespace-pre-line hover:underline"
                  >
                    {s.title}
                  </a>
                ) : (
                  <div className="text-sm font-semibold leading-normal text-black whitespace-pre-line">{s.title}</div>
                )}
                <div className="text-sm font-normal leading-normal text-black opacity-60 whitespace-pre-line">
                  {s.address || s.phone}
                </div>
              </div>

              <Button
                className="w-max py-2 px-4"
                onClick={() => {
                  setState("selectedCompany", selectedCompany?.data_id == s?.data_id ? null : s);
                }}
                variant={selectedCompany?.data_id == s.data_id ? "dark" : "outline"}
              >
                {selectedCompany?.data_id == s.data_id ? "Selected" : "Select"}
              </Button>
            </li>
          ))}
      </ul>
      <p className="text-left text-ash-500">
        Can&apos;t see your results?{" "}
        <button className="font-bold text-black" onClick={goBack}>
          Go Back
        </button>
      </p>
      <Button
        onClick={onSubmit}
        className="w-full mt-4 bg-black text-white text-base font-semibold"
        size="lg"
        disabled={!selectedCompany?.data_id}
      >
        Generate QR code
      </Button>
    </>
  );
}

const emailValidationSchema = z.object({
  email: z.string().min(1, { message: "Email is required" }).email("This is not a valid email."),
});

export function ShareQR() {
  const { QRCodeUrl, companyName, type } = useQR();
  const { nextStep } = useWizard();

  const form = useForm({
    resolver: zodResolver(emailValidationSchema),
    defaultValues: {
      email: "",
    },
  });

  const onFormSubmit = async (data) => {
    await handleApiRequest({
      makeRequest: async () => {
        return sendEmailWithQRCode({
          email: data.email,
          QRCodeUrl,
          companyName,
          type,
        });
      },
      successCallback: () => {
        nextStep("sentQR");
      },
    });
  };

  return (
    <div className="flex flex-col items-center">
      <div className="flex justify-center bg-rebolt-300 w-full rounded-md py-5 mb-6">
        <QRCodeSVG className="w-32 h-32" value={QRCodeUrl} />
      </div>
      <p className="text-xl font-semibold">
        QR code for {companyName} has been generated: <br />
      </p>
      <a href={QRCodeUrl} target="_blank" className="underline break-all text-sm text-rebolt-500">
        {QRCodeUrl}
      </a>
      <p className="font-medium mt-6 text-ash-600 tracking-tight">
        Save the QR code for later by sending it to your email:
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)} className="mt-4 w-full">
          <EditorInputField
            required
            className="mb-0"
            form={form}
            fieldName="email"
            inputClassName="mt-1 h-9 text-sm"
            placeholder="Your email address"
          />
          <FormButton
            formState={form.formState}
            size="lg"
            className="w-full mt-4 bg-black text-white text-base font-semibold"
          >
            Send QR to my Email
          </FormButton>
        </form>
      </Form>
    </div>
  );
}

export function SentQR() {
  const { QRCodeUrl, setState } = useQR();

  return (
    <div className="flex flex-col items-center">
      <div className="flex justify-center bg-rebolt-300 w-full rounded-md py-3 mb-6">
        <QRCodeSVG className="w-32 h-32" value={QRCodeUrl} />
      </div>
      <div className="flex flex-col items-center gap-4 w-full">
        <Image alt="Globe" width={24} height={24} className="w-6 h-6" src="/globe.svg" />
        <p className="text-xl font-semibold">The QR code was sent to your inbox!</p>
        <Link href="/" className="w-full">
          <Button
            onClick={() => {
              setState("selectedCompany", null);
            }}
            className="w-full bg-black text-white text-base font-semibold"
            size="lg"
          >
            Go back to the website
          </Button>
        </Link>
      </div>
    </div>
  );
}
